import { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { analytics } from './firebase';
import { logEvent } from "firebase/analytics";
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import SimpleLanding from './components/web/SimpleLanding';
import NotFound from './components/web/NotFound';
import TicTacToePrivacyPolicy from './components/apps/tictactoe-infinite/privacy';
import TicTacToeRedirect from './components/apps/tictactoe-infinite/Redirect';
import CredstonePrivacyPolicy from './components/apps/credstone/privacy';
import CredstoneRedirect from './components/apps/credstone/Redirect';
import DutyVigilLegal from './components/apps/dutyvigil/legal';
import DutyVigilRedirect from './components/apps/dutyvigil/Redirect';
import TeamLinkLegal from './components/apps/teamlink/legal';
import TeamLinkRedirect from './components/apps/teamlink/Redirect';
import TeamLinkUninstallSurvey from './components/apps/teamlink/UninstallSurvey';
import Legal from './components/web/Legal';
import BlogHome from './components/blog/BlogHome';
import BlogPost from './components/blog/BlogPost';

const defaultLogo = '/logo.png';
const defaultIcon = '/logo.ico';
const tictactoeShareImage = '/apps/tictactoe-infinite/logo.png';
const tictactoeIcon = '/apps/tictactoe-infinite/logo.ico';
const dutyvigilShareImage = '/apps/dutyvigil/logo.png';
const dutyvigilIcon = '/apps/dutyvigil/logo.ico';
const teamlinkShareImage = '/apps/teamlink/logo.png';
const teamlinkIcon = '/apps/teamlink/logo.png';

interface MetaData {
  title: string;
  description: string;
  keywords: string;
  image: string;
  icon: string;
  noindex?: boolean;
}

const pageMetaData: Record<string, MetaData> = {
  '/': { title: 'Tattva Dynamics - Essence. Engineered.', description: 'Discover innovative solutions and cutting-edge technology with Tattva Dynamics.', keywords: 'tattva dynamics, technology, innovation, engineering, apps', image: defaultLogo, icon: defaultIcon },
  '/tictactoe': { title: 'Tic Tac Toe - Infinite', description: 'Shifting Strategy game.', keywords: 'tictactoe, game, strategy', image: tictactoeShareImage, icon: tictactoeIcon },
  '/credstone': { title: 'c r e d s t o n e', description: 'Understand and track credit card milestones, offers, and more.', keywords: 'credstone, finance, credit card, milestones, offers, finance management', image: '/apps/credstone/logo.png', icon: '/apps/credstone/logo.png' },
  '/dutyvigil': { title: 'DutyVigil', description: 'Fatigue-Aware Duty Allocation.', keywords: 'dutyvigil, scheduling, fatigue', image: dutyvigilShareImage, icon: dutyvigilIcon },
  '/teamlink': { title: 'TeamLink', description: 'Sync, share, and search bookmarks across devices and teams. Build your knowledge bank.', keywords: 'teamlink, bookmarks, productivity', image: teamlinkShareImage, icon: teamlinkIcon },
  '/teamlink/privacy': { title: 'TeamLink - Privacy Policy', description: 'Privacy Policy for TeamLink.', keywords: 'teamlink, privacy', image: teamlinkShareImage, icon: teamlinkIcon },
  '/teamlink/legal': { title: 'TeamLink - Legal Documentation', description: 'Legal for TeamLink.', keywords: 'teamlink, legal', image: teamlinkShareImage, icon: teamlinkIcon },
  '/survey/extension/uninstall.html': { title: 'Teamlink - Uninstall Survey', description: 'We are sorry to see you go. Please let us know how we can improve.', keywords: 'teamlink, uninstall, survey', image: teamlinkShareImage, icon: teamlinkIcon, noindex: true },
  '/privacy': { title: 'Privacy Policy - Tattva Dynamics', description: 'Privacy Policy for Tattva Dynamics and its products.', keywords: 'tattva dynamics, privacy policy', image: defaultLogo, icon: defaultIcon },
  '/legal': { title: 'Legal Documentation - Tattva Dynamics', description: 'Legal terms and conditions for Tattva Dynamics.', keywords: 'tattva dynamics, legal, terms of service', image: defaultLogo, icon: defaultIcon },
  '/blog': { title: 'Blog | Tattva Dynamics', description: 'Latest updates and engineering insights.', keywords: 'blog, engineering, technology', image: defaultLogo, icon: defaultIcon }
};

function DynamicMeta({ pathname }: { pathname: string }) {
  const metaData = pageMetaData[pathname as keyof typeof pageMetaData] || pageMetaData['/'];
  const absoluteImageUrl = metaData.image.startsWith('http') ? metaData.image : window.location.origin + (metaData.image.startsWith('/') ? '' : '/') + metaData.image;
  
  return (
    <Helmet>
      <title>{metaData.title}</title>
      <link rel="icon" type="image/x-icon" href={metaData.icon} />
      <meta name="description" content={metaData.description} />
      <meta name="keywords" content={metaData.keywords} />
      {metaData.noindex && <meta name="robots" content="noindex, nosnippet" />}
      <meta property="og:title" content={metaData.title} />
      <meta property="og:image" content={absoluteImageUrl} />
    </Helmet>
  );
}

function App() {
  const hostname = window.location.hostname;

  const subdomainApp = useMemo(() => {
    if (hostname.includes('teamlink')) return 'teamlink';
    if (hostname.includes('dutyvigil')) return 'dutyvigil';
    if (hostname.includes('tictactoe')) return 'tictactoe';
    if (hostname.includes('credstone')) return 'credstone';
    return null;
  }, [hostname]);

  useEffect(() => {
    logEvent(analytics, 'page_view', {
      page_location: window.location.href,
      hostname: hostname
    });
  }, [hostname]);

  return (
    <Router future={{ v7_relativeSplatPath: true }}>
      <Routes>
        {subdomainApp === 'teamlink' && (
          <>
            <Route path="/" element={<SimpleLanding filterApp="TeamLink" />} />
            <Route path="/download" element={<TeamLinkRedirect />} />
            <Route path="/privacy" element={<TeamLinkLegal />} />
            <Route path="/legal" element={<TeamLinkLegal />} />
            <Route path="/survey/extension/uninstall.html" element={<><DynamicMeta pathname="/survey/extension/uninstall.html" /><TeamLinkUninstallSurvey /></>} />
            <Route path="/blog" element={<BlogHome project="teamlink" />} />
            <Route path="/blog/:slug" element={<BlogPost project="teamlink" />} />
          </>
        )}
        {subdomainApp === 'dutyvigil' && (
          <>
            <Route path="/" element={<SimpleLanding filterApp="DutyVigil" />} />
            <Route path="/download" element={<DutyVigilRedirect />} />
            <Route path="/privacy" element={<DutyVigilLegal />} />
            <Route path="/legal" element={<DutyVigilLegal />} />
            <Route path="/blog" element={<BlogHome project="dutyvigil" />} />
            <Route path="/blog/:slug" element={<BlogPost project="dutyvigil" />} />
          </>
        )}
        {subdomainApp === 'tictactoe' && (
          <>
            <Route path="/" element={<SimpleLanding filterApp="Tic Tac Toe" />} />
            <Route path="/download" element={<TicTacToeRedirect />} />
            <Route path="/privacy" element={<TicTacToePrivacyPolicy />} />
            <Route path="/legal" element={<TicTacToePrivacyPolicy />} />
            <Route path="/blog" element={<BlogHome project="tictactoe" />} />
            <Route path="/blog/:slug" element={<BlogPost project="tictactoe" />} />
          </>
        )}
        {subdomainApp === 'credstone' && (
          <>
            <Route path="/" element={<SimpleLanding filterApp="Credstone" />} />
            <Route path="/download" element={<CredstoneRedirect />} />
            <Route path="/privacy" element={<CredstonePrivacyPolicy />} />
            <Route path="/legal" element={<CredstonePrivacyPolicy />} />
            <Route path="/blog" element={<BlogHome project="credstone" />} />
            <Route path="/blog/:slug" element={<BlogPost project="credstone" />} />
          </>
        )}

        <Route path="/" element={<><DynamicMeta pathname="/" /><SimpleLanding /></>} />
        <Route path="/tictactoe" element={<TicTacToeRedirect />} />
        <Route path="/credstone" element={<CredstoneRedirect />} />
        <Route path="/dutyvigil" element={<DutyVigilRedirect />} />
        <Route path="/teamlink" element={<TeamLinkRedirect />} />
        
        <Route path="/tictactoe-infinite/privacy" element={<TicTacToePrivacyPolicy />} />
        <Route path="/credstone/privacy" element={<CredstonePrivacyPolicy />} />
        <Route path="/dutyvigil/privacy" element={<DutyVigilLegal />} />
        <Route path="/dutyvigil/legal" element={<DutyVigilLegal />} />
        <Route path="/teamlink/privacy" element={<TeamLinkLegal />} />
        <Route path="/teamlink/legal" element={<TeamLinkLegal />} />
        <Route path="/teamlink/survey/extension/uninstall.html" element={<><DynamicMeta pathname="/survey/extension/uninstall.html" /><TeamLinkUninstallSurvey /></>} />

        <Route path="/blog" element={<BlogHome project="tattvadynamics" />} />
        <Route path="/blog/:slug" element={<BlogPost project="tattvadynamics" />} />
        <Route path="/teamlink/blog" element={<BlogHome project="teamlink" />} />
        <Route path="/teamlink/blog/:slug" element={<BlogPost project="teamlink" />} />
        <Route path="/dutyvigil/blog" element={<BlogHome project="dutyvigil" />} />
        <Route path="/dutyvigil/blog/:slug" element={<BlogPost project="dutyvigil" />} />
        <Route path="/tictactoe-infinite/blog" element={<BlogHome project="tictactoe" />} />
        <Route path="/tictactoe-infinite/blog/:slug" element={<BlogPost project="tictactoe" />} />

        <Route path="/privacy" element={<Legal />} />
        <Route path="/legal" element={<Legal />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;
